import { Alert } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { MicroAppQueryResult } from "../../api/client.ts";

// Replacing a document that never came up is worth a couple of tries; past
// that the handle is not what is broken and retrying is just a slow loop.
export const MAX_DOCUMENT_REMINTS = 2;

// How long a sandbox document gets to announce itself before we treat its
// handle as dead and mint a new one. Generous: a heavy app doing its first
// paint is still well inside this.
const DOCUMENT_READY_TIMEOUT_MS = 8000;

/**
 * Renders an app that ships its own `client_code`.
 *
 * The document lives on a separate host that the session cookie never reaches,
 * and the server sends `Content-Security-Policy: sandbox`, so the frame is on
 * an opaque origin with no network capability. `sandbox="allow-scripts"` here
 * is the second of those two controls — never add `allow-same-origin`, which
 * would defeat both.
 *
 * Refreshed data is pushed in over postMessage rather than by remounting: the
 * frame only reloads when the app is published anew and the backend mints a
 * new document. `targetOrigin` has to be "*" — the frame is an opaque origin,
 * so there is no origin string to name, and `runtime.js` posts upward the same
 * way for the same reason.
 */
export function SandboxFrame({
  src,
  title,
  slug,
  version,
  fetchedAt,
  data,
  onDocumentLost,
  fill = false,
}: {
  src: string;
  title: string;
  slug: string;
  version: number;
  fetchedAt: string;
  data: Record<string, MicroAppQueryResult>;
  onDocumentLost: () => void;
  /** Take the whole viewport (TV) instead of sizing to the document's content. */
  fill?: boolean;
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(320);
  const [appError, setAppError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // The data the current document was minted with. Re-posting it on load would
  // fire every hive.onRefresh handler before anything had actually refreshed.
  const mountedAtRef = useRef(fetchedAt);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // The frame has an opaque origin, so e.origin is the string "null" and
      // is not a usable identity. Source identity is the correct check.
      if (e.source !== frameRef.current?.contentWindow) return;
      const msg = e.data ?? {};
      if (msg.type === "hive:ready") {
        setReady(true);
      } else if (msg.type === "hive:resize" && typeof msg.height === "number") {
        setHeight(Math.min(Math.max(msg.height, 120), 20000));
      } else if (msg.type === "hive:error" && typeof msg.message === "string") {
        setAppError(msg.message.slice(0, 500));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // A new document_url means a new document: the frame remounts and has to
  // announce itself again before it can be pushed to. Keyed on `src` alone —
  // adding fetchedAt would re-arm this on every refresh, and `ready` would
  // never come back, since a live frame does not reload to re-announce itself.
  const latestFetchedAt = useRef(fetchedAt);
  latestFetchedAt.current = fetchedAt;
  useEffect(() => {
    setAppError(null);
    setReady(false);
    // Record what the new document already carries, so its own data is not
    // pushed straight back at it on load.
    mountedAtRef.current = latestFetchedAt.current;
  }, [src]);

  // A document handle is single-use with a 60s TTL, so a frame can end up
  // pointed at one that is already gone (replayed, expired, or missing from
  // another pod's fallback store). The sandbox host answers that with a 404
  // body, which an iframe reports as a perfectly normal `load` — and the
  // origin is opaque, so there is nothing to read. A `hive:ready` that never
  // arrives is the only signal available; ask for a fresh document.
  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(onDocumentLost, DOCUMENT_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ready, src, onDocumentLost]);

  useEffect(() => {
    if (!ready || fetchedAt === mountedAtRef.current) return;
    frameRef.current?.contentWindow?.postMessage(
      { type: "hive:data", data, meta: { slug, title, version, fetched_at: fetchedAt } },
      "*",
    );
  }, [ready, data, fetchedAt, slug, title, version]);

  return (
    <>
      {appError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {appError}
        </Alert>
      ) : null}
      <iframe
        ref={frameRef}
        key={src}
        src={src}
        title={title}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        style={{ width: "100%", height: fill ? "100vh" : height, border: 0, display: "block" }}
      />
    </>
  );
}
